import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getSupabase } from "@/lib/hooks/api/utils";

export interface ContactByDomain {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  company_name: string | null;
  status: string | null;
  contact_type: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ContactsByDomainResult {
  contacts: ContactByDomain[];
  domain: string;
  totalContacts: number;
  searchDate: string;
}

export function useContactsByDomain(domain: string | null, excludeContactId?: string) {
  const query = useQuery({
    queryKey: ["contacts-by-domain", domain, excludeContactId],
    queryFn: async (): Promise<ContactsByDomainResult> => {
      if (!domain) {
        return {
          contacts: [],
          domain: "",
          totalContacts: 0,
          searchDate: new Date().toISOString(),
        };
      }

      const supabase = getSupabase();

      // Get current user information
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Build the query to find contacts with the same email domain
      // Using the same pattern as useContactsPaginated for email search
      let countQuery = supabase.from("contacts").select("*", { count: "exact", head: true });
      let dataQuery = supabase.from("contacts").select("*");

      // Filter by email domain using ilike pattern (same as emailSearch in contact search)
      // Search both primary email and aliases
      const domainPattern = `%@${domain}`;
      countQuery = countQuery.or(`email.ilike.${domainPattern},email_aliases.cs.{"*@${domain}"}`);
      dataQuery = dataQuery.or(`email.ilike.${domainPattern},email_aliases.cs.{"*@${domain}"}`);

      // Exclude the current contact if specified
      if (excludeContactId) {
        countQuery = countQuery.neq("id", excludeContactId);
        dataQuery = dataQuery.neq("id", excludeContactId);
      }

      // Only show active contacts
      countQuery = countQuery.eq("is_active", true);
      dataQuery = dataQuery.eq("is_active", true);

      // Get total count
      const { count, error: countError } = await countQuery;
      if (countError) {
        console.error("Error fetching contacts count by domain:", countError);
        throw new Error(countError.message);
      }

      // Apply ordering and limit
      dataQuery = dataQuery.order("created_at", { ascending: false }).limit(10);

      const { data: contacts, error } = await dataQuery;

      if (error) {
        console.error("Error fetching contacts by domain:", error);
        throw new Error(error.message);
      }

      return {
        contacts: contacts || [],
        domain,
        totalContacts: count || 0,
        searchDate: new Date().toISOString(),
      };
    },
    enabled: !!domain,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  const processedContacts = useMemo(() => {
    if (!query.data?.contacts) return [];

    return query.data.contacts.map((contact) => ({
      ...contact,
      displayName:
        [contact.first_name, contact.last_name].filter(Boolean).join(" ") || contact.email,
      fullName: [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "Unknown",
    }));
  }, [query.data?.contacts]);

  return {
    ...query,
    contacts: processedContacts,
    domain: query.data?.domain || "",
    totalContacts: query.data?.totalContacts || 0,
  };
}

// Utility function to extract domain from email
export function extractDomainFromEmail(email: string): string | null {
  if (!email || !email.includes("@")) return null;
  return email.split("@")[1]?.toLowerCase() || null;
}
